import React, { useEffect, useState, useCallback } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdClose } from 'react-icons/md';
import { frenchAreas } from 'variables/industries';

// Region filter options (same as the /bo/leads no_match tab): climate zones (H1/H2/H3) plus every
// French department — "Toute la France" is the filter's own default (empty selection).
const NO_MATCH_REGION_OPTIONS = frenchAreas.filter((area) => !area.toLowerCase().includes('toute'));

interface TestAccount {
    _id: string;
    email: string;
    name?: string;
    surname?: string;
    type: string;
}

interface DebugOffer {
    _id: string;
    offerNumber?: string;
    offerBuyerNumber?: string;
    industry?: string;
    subIndustry?: string;
    status?: string;
    sellerId?: { _id: string; email: string; name?: string } | null;
    buyerId?: { _id: string; email: string; name?: string } | null;
    testLeadCount: number;
    createdAt: string;
}

interface Industry {
    name: string;
    subIndustries: { name: string }[];
}

interface MatchCandidateCheck {
    pass: boolean;
    // The offer/PO's own value for this attribute.
    offerValue?: any;
    // What offerValue needed to be for a match — the lead/seller's value for filters that
    // compare against the lead, or a fixed platform requirement otherwise (see leadLabel).
    leadValue?: any;
    // What leadValue represents: "Lead", "Vendeur", "Requis", "Limite", etc.
    leadLabel?: string;
}

interface MatchCandidate {
    offerType: 'Offer' | 'SellerOffer' | 'PurchaseOrder';
    offerId: string;
    offerNumber?: string;
    name?: string;
    ownerId?: string;
    status?: string;
    isAutoSuspended: boolean;
    buyerPrice?: number;
    buyerEmail?: string | null;
    checks: Record<string, MatchCandidateCheck>;
    failedFilters: string[];
    priceGap?: number | null;
}

interface LeadMatchDiagnostic {
    _id: string;
    criteria: {
        industry: string;
        subIndustry?: string;
        region?: string;
        channels?: string[];
        minimumPrice?: number;
        // False when `industry` matched no Industry catalog entry — a stale/mistyped name
        // rather than a genuine lack of demand. Undefined for diagnostics recorded before
        // this field existed.
        industryRecognized?: boolean;
    };
    candidates: MatchCandidate[];
    closestMiss?: { offerType: string; offerId: string; failedFilters: string[] } | null;
    createdAt: string;
}

interface NoMatchLead {
    _id: string;
    createdAt: string;
    sideSellerId?: { _id: string; email: string; name?: string } | null;
    formTemplateId?: string;
    landingPage?: string;
    invalidReason?: string;
    email?: string;
    channels?: string[];
    diagnostic: LeadMatchDiagnostic | null;
    // Best-known industry/sub-industry for this lead — from the diagnostic when there is one,
    // otherwise resolved server-side from the matched offer or the lead's form template.
    industry?: string | null;
    subIndustry?: string | null;
}

const DEBUG_TABS = [
    { key: 'offers', label: 'Test offers' },
    { key: 'noMatch', label: 'No match leads' },
] as const;
type DebugTab = typeof DEBUG_TABS[number]['key'];

export default function BackOfficeDebug() {
    const [activeTab, setActiveTab] = useState<DebugTab>('offers');

    const [sellers, setSellers] = useState<TestAccount[]>([]);
    const [buyers, setBuyers] = useState<TestAccount[]>([]);
    const [offers, setOffers] = useState<DebugOffer[]>([]);
    const [industries, setIndustries] = useState<Industry[]>([]);
    const [loading, setLoading] = useState(true);
    const [creatingAccount, setCreatingAccount] = useState<'seller' | 'buyer' | null>(null);

    const [noMatchLeads, setNoMatchLeads] = useState<NoMatchLead[]>([]);
    const [noMatchLoading, setNoMatchLoading] = useState(false);
    const [expandedLeadId, setExpandedLeadId] = useState<string | null>(null);
    const [noMatchIndustryFilter, setNoMatchIndustryFilter] = useState('');
    const [noMatchSubIndustryFilter, setNoMatchSubIndustryFilter] = useState('');
    const [noMatchRegionFilter, setNoMatchRegionFilter] = useState('');

    const [selectedSellerId, setSelectedSellerId] = useState('');
    const [selectedBuyerId, setSelectedBuyerId] = useState('');
    const [selectedIndustry, setSelectedIndustry] = useState('');
    const [selectedSubIndustry, setSelectedSubIndustry] = useState('');
    const [creatingOffer, setCreatingOffer] = useState(false);

    const [quantities, setQuantities] = useState<Record<string, number>>({});
    const [generatingId, setGeneratingId] = useState<string | null>(null);

    const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

    const fetchAll = useCallback(async () => {
        try {
            setLoading(true);
            const [accountsRes, offersRes, industriesRes] = await Promise.all([
                axios.get(`${CONFIG.apiUrl}/admin/debug/test-accounts`, { headers: authHeaders() }),
                axios.get(`${CONFIG.apiUrl}/admin/debug/offers`, { headers: authHeaders() }),
                axios.get(`${CONFIG.apiUrl}/admin/industries-list`, { headers: authHeaders() }),
            ]);
            setSellers(accountsRes.data.sellers);
            setBuyers(accountsRes.data.buyers);
            setOffers(offersRes.data.offers);
            setIndustries(industriesRes.data.industries);
        } catch (error) {
            console.error('Error fetching debug data:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const fetchNoMatchLeads = useCallback(async () => {
        try {
            setNoMatchLoading(true);
            const params: Record<string, string> = {};
            if (noMatchIndustryFilter) params.industry = noMatchIndustryFilter;
            if (noMatchSubIndustryFilter) params.subIndustry = noMatchSubIndustryFilter;
            if (noMatchRegionFilter) params.region = noMatchRegionFilter;
            const res = await axios.get(`${CONFIG.apiUrl}/admin/debug/no-match-leads`, { headers: authHeaders(), params });
            setNoMatchLeads(res.data.leads);
        } catch (error) {
            console.error('Error fetching no-match leads:', error);
        } finally {
            setNoMatchLoading(false);
        }
    }, [noMatchIndustryFilter, noMatchSubIndustryFilter, noMatchRegionFilter]);

    useEffect(() => {
        if (activeTab === 'noMatch') {
            fetchNoMatchLeads();
        }
    }, [activeTab, fetchNoMatchLeads]);

    const handleCreateAccount = async (type: 'seller' | 'buyer') => {
        try {
            setCreatingAccount(type);
            await axios.post(`${CONFIG.apiUrl}/admin/debug/test-accounts`, { type }, { headers: authHeaders() });
            await fetchAll();
        } catch (error) {
            console.error('Error creating test account:', error);
        } finally {
            setCreatingAccount(null);
        }
    };

    const handleCreateOffer = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedSellerId || !selectedBuyerId) return;
        try {
            setCreatingOffer(true);
            await axios.post(
                `${CONFIG.apiUrl}/admin/debug/offers`,
                {
                    sellerId: selectedSellerId,
                    buyerId: selectedBuyerId,
                    industry: selectedIndustry || undefined,
                    subIndustry: selectedSubIndustry || undefined,
                },
                { headers: authHeaders() },
            );
            await fetchAll();
        } catch (error) {
            console.error('Error creating debug offer:', error);
        } finally {
            setCreatingOffer(false);
        }
    };

    const handleGenerateLeads = async (offerId: string) => {
        try {
            setGeneratingId(offerId);
            const quantity = quantities[offerId] || 1;
            await axios.post(
                `${CONFIG.apiUrl}/admin/debug/offers/${offerId}/generate-leads`,
                { quantity },
                { headers: authHeaders() },
            );
            await fetchAll();
        } catch (error) {
            console.error('Error generating test leads:', error);
        } finally {
            setGeneratingId(null);
        }
    };

    const handleDeleteOffer = async (offerId: string) => {
        if (!window.confirm('Delete this debug offer and its test leads?')) return;
        try {
            await axios.delete(`${CONFIG.apiUrl}/admin/debug/offers/${offerId}`, { headers: authHeaders() });
            setOffers((prev) => prev.filter((o) => o._id !== offerId));
        } catch (error) {
            console.error('Error deleting debug offer:', error);
        }
    };

    const subIndustriesFor = (industryName: string) =>
        industries.find((ind) => ind.name === industryName)?.subIndustries || [];

    const formatDate = (iso: string) => new Date(iso).toLocaleString();

    const formatCheckValue = (v: any): string => {
        if (v === undefined || v === null || v === '') return '-';
        if (Array.isArray(v)) return v.length ? v.join(', ') : '(aucun)';
        return String(v);
    };

    // PurchaseOrder never uses the literal 'running' status (its equivalent is 'active') —
    // candidates that are already stopped aren't interesting here since the whole point of
    // this view is to see why a currently-running offer/PO still didn't match.
    const isRunningCandidate = (c: MatchCandidate) =>
        c.offerType === 'PurchaseOrder' ? c.status === 'active' : c.status === 'running';

    return (
        <div className="pt-[50px]">
            <div className="mb-5 flex gap-2 border-b border-gray-200 dark:border-navy-600">
                {DEBUG_TABS.map((tab) => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${
                            activeTab === tab.key
                                ? 'border-brand-500 text-brand-500'
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'offers' && (
            <>
            <Card extra="p-5 mb-5">
                <div className="mb-4 text-2xl font-bold text-navy-700 dark:text-white">Debug</div>
                <div className="mb-5 text-sm text-gray-600 dark:text-gray-400">
                    Create test accounts and a "running" test offer between them, then generate synthetic
                    leads (leadQualityStatus: test) to try out flows without touching real data or billing.
                </div>

                {/* Test accounts */}
                <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                        <div className="mb-2 flex items-center justify-between">
                            <div className="text-sm font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Test sellers</div>
                            <button
                                onClick={() => handleCreateAccount('seller')}
                                disabled={creatingAccount === 'seller'}
                                className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                            >
                                {creatingAccount === 'seller' ? 'Creating...' : '+ Create test seller'}
                            </button>
                        </div>
                        <ul className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 dark:border-navy-600">
                            {sellers.length === 0 && (
                                <li className="p-2 text-sm text-gray-400">No test sellers yet</li>
                            )}
                            {sellers.map((s) => (
                                <li key={s._id} className="border-b border-gray-100 p-2 text-sm last:border-b-0 dark:border-navy-700">
                                    {s.email}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <div className="mb-2 flex items-center justify-between">
                            <div className="text-sm font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Test buyers</div>
                            <button
                                onClick={() => handleCreateAccount('buyer')}
                                disabled={creatingAccount === 'buyer'}
                                className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                            >
                                {creatingAccount === 'buyer' ? 'Creating...' : '+ Create test buyer'}
                            </button>
                        </div>
                        <ul className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 dark:border-navy-600">
                            {buyers.length === 0 && (
                                <li className="p-2 text-sm text-gray-400">No test buyers yet</li>
                            )}
                            {buyers.map((b) => (
                                <li key={b._id} className="border-b border-gray-100 p-2 text-sm last:border-b-0 dark:border-navy-700">
                                    {b.email}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Create debug offer */}
                <form onSubmit={handleCreateOffer} className="mb-2 flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 p-4 dark:border-navy-600">
                    <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">Seller</label>
                        <select
                            value={selectedSellerId}
                            onChange={(e) => setSelectedSellerId(e.target.value)}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-800"
                            required
                        >
                            <option value="">Select test seller</option>
                            {sellers.map((s) => <option key={s._id} value={s._id}>{s.email}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">Buyer</label>
                        <select
                            value={selectedBuyerId}
                            onChange={(e) => setSelectedBuyerId(e.target.value)}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-800"
                            required
                        >
                            <option value="">Select test buyer</option>
                            {buyers.map((b) => <option key={b._id} value={b._id}>{b.email}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">Industry</label>
                        <select
                            value={selectedIndustry}
                            onChange={(e) => { setSelectedIndustry(e.target.value); setSelectedSubIndustry(''); }}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-800"
                        >
                            <option value="">Select industry</option>
                            {industries.map((ind) => <option key={ind.name} value={ind.name}>{ind.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">Sub-industry</label>
                        <select
                            value={selectedSubIndustry}
                            onChange={(e) => setSelectedSubIndustry(e.target.value)}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-800"
                            disabled={!selectedIndustry}
                        >
                            <option value="">Select sub-industry</option>
                            {subIndustriesFor(selectedIndustry).map((sub) => (
                                <option key={sub.name} value={sub.name}>{sub.name}</option>
                            ))}
                        </select>
                    </div>
                    <button
                        type="submit"
                        disabled={creatingOffer || !selectedSellerId || !selectedBuyerId}
                        className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                    >
                        {creatingOffer ? 'Creating...' : 'Create debug offer'}
                    </button>
                </form>
            </Card>

            <Card extra="p-5">
                <div className="mb-4 text-lg font-bold text-navy-700 dark:text-white">Debug offers</div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-navy-600">
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Offer</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Seller</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Buyer</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Secteur</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Status</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Test leads</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={7} className="py-8 text-center text-sm text-gray-500">Loading...</td></tr>
                            ) : offers.length === 0 ? (
                                <tr><td colSpan={7} className="py-8 text-center text-sm text-gray-500">No debug offers yet</td></tr>
                            ) : (
                                offers.map((offer) => (
                                    <tr key={offer._id} className="border-b border-gray-100 dark:border-navy-700">
                                        <td className="py-3 pr-4 font-mono text-xs text-navy-700 dark:text-white">
                                            {offer.offerNumber || offer._id.slice(-8)}
                                        </td>
                                        <td className="py-3 pr-4 text-sm">{offer.sellerId?.email || '-'}</td>
                                        <td className="py-3 pr-4 text-sm">{offer.buyerId?.email || '-'}</td>
                                        <td className="py-3 pr-4 text-sm">
                                            {offer.industry || '-'}{offer.subIndustry ? ` / ${offer.subIndustry}` : ''}
                                        </td>
                                        <td className="py-3 pr-4 text-sm">
                                            <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                {offer.status}
                                            </span>
                                        </td>
                                        <td className="py-3 pr-4 text-sm">{offer.testLeadCount}</td>
                                        <td className="py-3 pr-4">
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    min={1}
                                                    max={500}
                                                    value={quantities[offer._id] ?? 1}
                                                    onChange={(e) => setQuantities((prev) => ({ ...prev, [offer._id]: Number(e.target.value) }))}
                                                    className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-sm dark:border-navy-600 dark:bg-navy-800"
                                                />
                                                <button
                                                    onClick={() => handleGenerateLeads(offer._id)}
                                                    disabled={generatingId === offer._id}
                                                    className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                                                >
                                                    {generatingId === offer._id ? 'Generating...' : 'Generate leads'}
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteOffer(offer._id)}
                                                    className="rounded-lg bg-red-100 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-200 dark:bg-red-900 dark:text-red-200"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>
            </>
            )}

            {activeTab === 'noMatch' && (
            <Card extra="p-5">
                <div className="mb-4 flex items-center justify-between">
                    <div className="text-lg font-bold text-navy-700 dark:text-white">No match leads</div>
                    <button
                        onClick={() => fetchNoMatchLeads()}
                        disabled={noMatchLoading}
                        className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                    >
                        {noMatchLoading ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
                <div className="mb-5 text-sm text-gray-600 dark:text-gray-400">
                    Leads that came in through a side-seller's generic form but matched no offer or purchase
                    order. Expand a row to see every candidate offer/PO sharing the lead's industry and which
                    filter(s) excluded each one.
                </div>
                <div className="mb-4 flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Secteur</label>
                        <select
                            value={noMatchIndustryFilter}
                            onChange={(e) => { setNoMatchIndustryFilter(e.target.value); setNoMatchSubIndustryFilter(''); }}
                            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                        >
                            <option value="">Tous</option>
                            {industries.map((ind) => (
                                <option key={ind.name} value={ind.name}>{ind.name}</option>
                            ))}
                        </select>
                    </div>
                    {noMatchIndustryFilter && (
                        <div className="flex items-center gap-2">
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Sous-secteur</label>
                            <select
                                value={noMatchSubIndustryFilter}
                                onChange={(e) => setNoMatchSubIndustryFilter(e.target.value)}
                                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                            >
                                <option value="">Tous</option>
                                {subIndustriesFor(noMatchIndustryFilter).map((sub) => (
                                    <option key={sub.name} value={sub.name}>{sub.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Région</label>
                        <select
                            value={noMatchRegionFilter}
                            onChange={(e) => setNoMatchRegionFilter(e.target.value)}
                            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                        >
                            <option value="">🇫🇷 Toute la France</option>
                            {NO_MATCH_REGION_OPTIONS.map((area) => (
                                <option key={area} value={area}>{area}</option>
                            ))}
                        </select>
                    </div>
                    {(noMatchIndustryFilter || noMatchSubIndustryFilter || noMatchRegionFilter) && (
                        <button
                            type="button"
                            onClick={() => { setNoMatchIndustryFilter(''); setNoMatchSubIndustryFilter(''); setNoMatchRegionFilter(''); }}
                            className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                        >
                            <MdClose size={14} /> Effacer filtres
                        </button>
                    )}
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-navy-600">
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Date</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Side seller</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Secteur</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Region</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Reason</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Candidates</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {noMatchLoading ? (
                                <tr><td colSpan={7} className="py-8 text-center text-sm text-gray-500">Loading...</td></tr>
                            ) : noMatchLeads.length === 0 ? (
                                <tr><td colSpan={7} className="py-8 text-center text-sm text-gray-500">No "no_match" leads</td></tr>
                            ) : (
                                noMatchLeads.map((lead) => {
                                    const isExpanded = expandedLeadId === lead._id;
                                    const runningCandidates = (lead.diagnostic?.candidates || []).filter(isRunningCandidate);
                                    const closest = runningCandidates[0];
                                    return (
                                        <React.Fragment key={lead._id}>
                                            <tr className="border-b border-gray-100 dark:border-navy-700">
                                                <td className="py-3 pr-4 text-xs text-gray-500">{formatDate(lead.createdAt)}</td>
                                                <td className="py-3 pr-4 text-sm">{lead.sideSellerId?.email || '-'}</td>
                                                <td className="py-3 pr-4 text-sm">
                                                    <span title={lead.diagnostic?.criteria.industryRecognized === false ? "Industrie inconnue du catalogue — nom probablement obsolète ou mal orthographié" : undefined}>
                                                        {lead.industry || '-'}
                                                        {lead.subIndustry ? ` / ${lead.subIndustry}` : ''}
                                                        {lead.diagnostic?.criteria.industryRecognized === false && (
                                                            <span className="ml-1 inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-900 dark:text-red-200">
                                                                industrie inconnue
                                                            </span>
                                                        )}
                                                    </span>
                                                </td>
                                                <td className="py-3 pr-4 text-sm">{lead.diagnostic?.criteria.region || '-'}</td>
                                                <td className="py-3 pr-4 max-w-xs truncate text-sm" title={lead.invalidReason}>
                                                    {lead.invalidReason || '-'}
                                                </td>
                                                <td className="py-3 pr-4 text-sm">
                                                    {lead.diagnostic
                                                        ? runningCandidates.length > 0
                                                            ? `${runningCandidates.length}${closest ? ` (le + proche: ${closest.failedFilters.length} filtre(s) en échec)` : ''}`
                                                            : lead.diagnostic.criteria.industryRecognized === false
                                                                ? "0 — aucune offre/commande ne référence cette industrie, et elle n'existe pas dans le catalogue"
                                                                : "0 — aucune offre/commande active pour cette industrie/sous-industrie en ce moment"
                                                        : 'Pas de diagnostic'}
                                                </td>
                                                <td className="py-3 text-sm">
                                                    {lead.diagnostic && runningCandidates.length > 0 && (
                                                        <button
                                                            onClick={() => setExpandedLeadId(isExpanded ? null : lead._id)}
                                                            className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-200"
                                                        >
                                                            {isExpanded ? 'Hide' : 'Details'}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                            {isExpanded && lead.diagnostic && (
                                                <tr className="border-b border-gray-100 dark:border-navy-700">
                                                    <td colSpan={7} className="bg-gray-50 p-4 dark:bg-navy-800">
                                                        <div className="overflow-x-auto">
                                                            <table className="w-full">
                                                                <thead>
                                                                    <tr className="text-left text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                                        <th className="pb-2 pr-4">Type</th>
                                                                        <th className="pb-2 pr-4">Offer</th>
                                                                        <th className="pb-2 pr-4">Status</th>
                                                                        <th className="pb-2 pr-4">Buyer email</th>
                                                                        <th className="pb-2 pr-4">Buyer price</th>
                                                                        <th className="pb-2 pr-4">Price gap</th>
                                                                        <th className="pb-2">Failed filters</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {runningCandidates.map((c) => (
                                                                        <tr key={`${c.offerType}-${c.offerId}`} className="border-t border-gray-200 dark:border-navy-700">
                                                                            <td className="py-2 pr-4 text-xs">{c.offerType}</td>
                                                                            <td className="py-2 pr-4 font-mono text-xs">{c.offerNumber || c.name || c.offerId.slice(-8)}</td>
                                                                            <td className="py-2 pr-4 text-xs">{c.status}{c.isAutoSuspended ? ' (suspended)' : ''}</td>
                                                                            <td className="py-2 pr-4 text-xs">{c.buyerEmail || '-'}</td>
                                                                            <td className="py-2 pr-4 text-xs">{c.buyerPrice != null ? `${c.buyerPrice} €` : '-'}</td>
                                                                            <td className="py-2 pr-4 text-xs">{c.priceGap != null ? `-${c.priceGap} €` : '-'}</td>
                                                                            <td className="py-2 text-xs">
                                                                                {c.failedFilters.length === 0 ? (
                                                                                    <span className="text-gray-400">-</span>
                                                                                ) : (
                                                                                    <div className="flex flex-col gap-1">
                                                                                        {c.failedFilters.map((f) => {
                                                                                            const check = c.checks[f];
                                                                                            return (
                                                                                                <div key={f} className="flex flex-wrap items-center gap-1.5">
                                                                                                    <span className="inline-flex shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-900 dark:text-red-200">
                                                                                                        {f}
                                                                                                    </span>
                                                                                                    {check && (
                                                                                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                                                            Offre: {formatCheckValue(check.offerValue)} · {check.leadLabel || 'Lead'}: {formatCheckValue(check.leadValue)}
                                                                                                        </span>
                                                                                                    )}
                                                                                                </div>
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>
            )}
        </div>
    );
}
